import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { createApiUrl } from '../utils/config';

/**
 * SiteSettingsContext - Manages site settings (logo, cart, site info)
 * NOTE: Theme/color parameters removed - using PageStyleEditor instead
 */
const SiteSettingsContext = createContext();

export const useSiteSettings = () => {
  const context = useContext(SiteSettingsContext);
  if (!context) {
    // Return default values if no provider
    return {
      siteSettings: {},
      isLoading: false,
      refreshSiteSettings: () => {}
    };
  }
  return context;
};

// For backward compatibility - alias for useSiteSettings
export const useTheme = () => {
  const context = useSiteSettings();
  return {
    ...context,
    // Legacy properties for backward compatibility
    currentTheme: 'default',
    isInitialized: true,
    isUpdatingSiteSettings: context.isLoading,
    customThemes: [],
    debugCustomThemes: () => {},
    refreshCustomThemes: () => {},
    changeTheme: () => {}
  };
};

export const SiteSettingsProvider = ({ children }) => {
  const [siteSettings, setSiteSettings] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  // Fetch site settings from backend
  const fetchSiteSettings = useCallback(async () => {
    try {
      const response = await fetch(createApiUrl('api/site-settings'));
      if (response.ok) {
        const data = await response.json();
        // Keep all settings - filtering removed to preserve functional settings
        setSiteSettings(data);
        return data; // Return the fetched data
      }
      return null;
    } catch (error) {
      console.warn('Failed to fetch site settings:', error);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchSiteSettings();
  }, [fetchSiteSettings]);

  const refreshSiteSettings = useCallback(async () => {
    setIsLoading(true);
    const newSettings = await fetchSiteSettings();
    return newSettings; // Return the NEW settings, not the stale state
  }, [fetchSiteSettings]);

  const value = {
    siteSettings,
    isLoading,
    isUpdatingSiteSettings: isLoading,
    isInitialized: !isLoading,
    refreshSiteSettings,
    // Legacy compatibility
    currentTheme: 'default',
    customThemes: [],
    debugCustomThemes: () => {},
    refreshCustomThemes: () => {},
    changeTheme: () => {}
  };

  return (
    <SiteSettingsContext.Provider value={value}>
      {children}
    </SiteSettingsContext.Provider>
  );
};

// Legacy export for ThemeProvider compatibility
export const ThemeProvider = SiteSettingsProvider;

export default SiteSettingsContext;








